import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cls } from '@/utils/cls';

const buttonVariants = cva(
  "aria-invalid:ring-red-500/20 aria-invalid:border-red-500 inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium outline-none transition-all focus-visible:border-neutral-950 focus-visible:ring focus-visible:ring-neutral-950/50 disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primaryOutline:
          'shadow-xs hover:text-primary-600/90 border bg-white text-primary-600 hover:bg-neutral-100 ',
        default: 'shadow-xs hover:bg-primary-600/90 bg-primary-600 text-white ',
        destructive:
          'shadow-xs bg-red-500 text-white hover:bg-red-500/90 focus-visible:ring-red-500/20 ',
        outline:
          'shadow-xs border bg-white hover:bg-neutral-100 hover:text-neutral-900 ',
        secondary:
          'shadow-xs bg-neutral-100 text-neutral-900 hover:bg-neutral-100/80 ',
        ghost: 'hover:bg-neutral-100 hover:text-neutral-900 ',
        link: 'text-neutral-900 underline-offset-4 hover:underline ',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<'button'> &
    VariantProps<typeof buttonVariants> & { asChild?: boolean }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp: React.ElementType = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cls(buttonVariants({ variant, size, className }))}
      ref={asChild ? (ref as any) : ref}
      {...props}
    />
  );
});

Button.displayName = 'Button';

export { Button, buttonVariants };
